/**
 * Het grafiekthema van NovaHub.
 *
 * Alle grafieken op het dashboard komen hier langs, zodat kleur, raster,
 * tooltip en animatie overal hetzelfde zijn. De kleuren komen uit de
 * stijlreferentie; ze staan hier als letterlijke waarden en niet als
 * CSS-variabele, omdat ECharts op een canvas tekent en `var(--acc)` daar
 * niets betekent.
 *
 * Uitgangspunten:
 *
 * * donkere ondergrond, rasterlijnen op 10% wit — nooit een harde witte lijn;
 * * lijnen lopen vloeiend en hebben een zachte verloopvulling eronder;
 * * staven zijn bovenaan afgerond en lopen van vol naar doorzichtig;
 * * de tooltip is een donkere kaart met een accentrand, en noemt de datum,
 *   de waarde en de eenheid.
 */

/** De accentkleuren, in de volgorde waarin reeksen ze krijgen. */
export const KLEUREN = {
  acc: '#a855f7',
  acc2: '#c084fc',
  acc3: '#7c3aed',
  teal: '#14b8a6',
  ok: '#3ddc97',
  warn: '#ffcf5c',
  pink: '#ff5fa2',
  blue: '#4aa8ff',
} as const

export const REEKSKLEUREN = [
  KLEUREN.acc, KLEUREN.teal, KLEUREN.ok, KLEUREN.warn,
  KLEUREN.pink, KLEUREN.blue, KLEUREN.acc2, KLEUREN.acc3,
]

const TEKST = '#9a92b8'
const RASTER = 'rgba(255,255,255,.10)'
const AS = 'rgba(255,255,255,.14)'

/** Vaste hoogtes per soort, zodat panelen niet gaan springen. */
export const HOOGTE = {
  uur: 220,
  dag: 260,
  sparkline: 48,
  trechter: 260,
  mix: 260,
} as const

/**
 * Een verticaal verloop van vol naar bijna doorzichtig.
 *
 * Voor een vlak onder een lijn (`vanaf` 0.35) of voor een staaf
 * (`vanaf` 0.9), zodat beide dezelfde taal spreken.
 */
export function verloop(kleur: string, vanaf = 0.35, tot = 0.02) {
  return {
    type: 'linear' as const,
    x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: mengKleur(kleur, vanaf) },
      { offset: 1, color: mengKleur(kleur, tot) },
    ],
  }
}

/** Een hexkleur met doorzichtigheid, als rgba-tekst. */
export function mengKleur(hex: string, alpha: number): string {
  const h = hex.replace('#', '')
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

/** De as-, raster- en tooltipinstellingen die elke grafiek deelt. */
export function basis(opties: {
  /** Eenheid achter de waarde in de tooltip en op de y-as. */
  eenheid?: string
  /** Opmaakfunctie voor een waarde; overschrijft de eenheid. */
  format?: (v: number) => string
  /** Ruimte onderaan voor een legenda. */
  legenda?: boolean
} = {}) {
  const toon = opties.format ?? ((v: number) => `${v}${opties.eenheid ?? ''}`)
  return {
    // Kort maar merkbaar: de grafiek bouwt zichzelf op zonder op te houden.
    animationDuration: 420,
    animationEasing: 'cubicOut' as const,
    backgroundColor: 'transparent',
    grid: {
      left: 52, right: 16, top: opties.legenda ? 34 : 14, bottom: 28,
      containLabel: false,
    },
    textStyle: { color: TEKST, fontSize: 11 },
    tooltip: {
      trigger: 'axis' as const,
      backgroundColor: 'rgba(22,16,41,.96)',
      borderColor: KLEUREN.acc,
      borderWidth: 1,
      padding: [8, 10],
      textStyle: { color: '#ece9f5', fontSize: 12 },
      axisPointer: {
        type: 'line' as const,
        lineStyle: { color: mengKleur(KLEUREN.acc, 0.5), width: 1 },
      },
      valueFormatter: (v: unknown) => toon(Number(v ?? 0)),
    },
    legend: opties.legenda
      ? {
          top: 0, right: 0, icon: 'roundRect', itemWidth: 10, itemHeight: 10,
          textStyle: { color: TEKST, fontSize: 11 },
        }
      : undefined,
  }
}

/** De x-as: leesbare labels, geen harde lijn. */
export function xAs(labels: string[], opties: { alle?: boolean } = {}) {
  return {
    type: 'category' as const,
    data: labels,
    boundaryGap: true,
    axisLine: { lineStyle: { color: AS } },
    axisTick: { show: false },
    axisLabel: {
      color: TEKST,
      fontSize: 10,
      // Bij veel punten niet elk label; ECharts kiest dan zelf een ritme.
      interval: opties.alle ? 0 : 'auto' as const,
      hideOverlap: true,
    },
  }
}

/** De y-as: eenheid erbij, rasterlijnen subtiel. */
export function yAs(format: (v: number) => string) {
  return {
    type: 'value' as const,
    axisLine: { show: false },
    axisTick: { show: false },
    splitLine: { lineStyle: { color: RASTER, type: 'solid' as const } },
    axisLabel: { color: TEKST, fontSize: 10, formatter: (v: number) => format(v) },
  }
}

/** Een vloeiende lijn met verloopvulling. */
export function lijnReeks(naam: string, data: number[], kleur: string, opties: {
  gevuld?: boolean
} = {}) {
  return {
    name: naam,
    type: 'line' as const,
    data,
    smooth: 0.35,
    // 28-10-2026 (Jordy): bij één of twee punten (bijv. "Today" per dag) is
    // een lijn zonder punten onzichtbaar; dan de punten wel tekenen.
    showSymbol: data.length <= 2,
    symbolSize: 7,
    lineStyle: { width: 2, color: kleur, shadowBlur: 10, shadowColor: mengKleur(kleur, 0.5) },
    itemStyle: { color: kleur },
    areaStyle: opties.gevuld === false ? undefined : { color: verloop(kleur) },
  }
}

/** Een staaf met afgeronde bovenkant, verloop en een lichte gloed. */
export function staafReeks(naam: string, data: number[], kleur: string) {
  return {
    name: naam,
    type: 'bar' as const,
    data,
    barMaxWidth: 26,
    itemStyle: {
      borderRadius: [4, 4, 0, 0],
      color: verloop(kleur, 0.95, 0.35),
      shadowBlur: 8,
      shadowColor: mengKleur(kleur, 0.35),
    },
  }
}
